import { motion } from "framer-motion";
import {
    Archive,
    Bookmark,
    Briefcase,
    CalendarCheck,
    CalendarClock,
    CheckCircle2,
    ClipboardList,
    Clock,
    Eye,
    FileText,
    Loader2,
    MapPin,
    MessageCircle,
    Phone,
    PhoneOff,
    RotateCcw,
    Sparkles,
    UserX,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { RISK_META, STATUS_META } from "../../lib/constants";
import { removeFromQueue } from "../../lib/callQueue";
import { initials, liveSignal, timeAgo, toE164, formatDate, rejectionReasonLabel } from "../../lib/format";
import CallButton from "../call/CallButton";
import PlayLastCallButton from "../call/PlayLastCallButton";
import FastTrackButton from "./FastTrackButton";
import ScoreRing from "./ScoreRing";
import SmartBadge from "./SmartBadge";
import SetAsideReasonModal from "./SetAsideReasonModal";
import TalentPoolModal from "./TalentPoolModal";

const OFFER_STATUS_TONE = {
    "Publiée": "bg-emerald-50 text-emerald-700 border-emerald-200",
    "Brouillon": "bg-amber-50 text-amber-800 border-amber-200",
    "Archivée": "bg-slate-100 text-slate-600 border-slate-200",
    "Programmée": "bg-blue-50 text-blue-700 border-blue-200",
};

const SIGNAL_TONE = {
    ok: "text-emerald-600",
    info: "text-blue-600",
    warn: "text-amber-600",
    alert: "text-shariti-red font-bold",
};

const DEFAULT_AVAILABILITIES = new Set(["À confirmer", "À évaluer", "—", "", null]);

/**
 * Compute a HIGH-VISIBILITY engagement badge for the candidate card.
 *
 * The goal is to let the recruiter glance at a candidate list and instantly
 * see "have I already tried to call this person? what happened?" without
 * having to read the small signal text.
 *
 * Returns either null (no useful state) or an object with:
 *   { label, sublabel, tone, icon }
 * where tone in: "warning" (called, no reply) | "info" (called, qualified) |
 * "success" (immersion booked) | "ok" (validated/onboarding)
 */
function engagementBadge(candidate) {
    const status = candidate.status;
    const updated = candidate.updated_at;
    // Reading the status_history would be more precise, but `updated_at` is
    // a good enough proxy for "last action" since the API touches it on each
    // qualifier / phone action.
    const since = updated ? timeAgo(updated).replace("il y a ", "il y a ") : null;
    const attempts = Number(candidate?.call_attempts || 0);
    const manualFromCall = Boolean(candidate?.manual_from_call);
    switch (status) {
        case "new":
            return null; // never been touched
        case "prerec_assigned":
            // iter92 · Manual-from-call candidates arrive with attempts=0 → they
            // haven't been called yet, so "Appelé · sans réponse" is misleading.
            // Show a neutral "À rappeler / Manuel" badge instead.
            if (attempts <= 0) {
                return {
                    label: manualFromCall ? "Manuel · à rappeler" : "À rappeler",
                    sublabel: since ? `créé ${since}` : null,
                    tone: "info",
                    icon: Phone,
                };
            }
            // ⇒ recruiter started a prerec but didn't finalise → most likely
            // "called but no answer / will call back"
            return {
                label: "Appelé · sans réponse",
                sublabel: since ? `dernier essai ${since}` : null,
                tone: "warning",
                icon: PhoneOff,
            };
        case "prerec_done":
            return {
                label: "Pré-rec effectué",
                sublabel: since ? `qualifié ${since}` : null,
                tone: "info",
                icon: Phone,
            };
        case "immersion_scheduled":
            return {
                label: "Immersion programmée",
                sublabel: since ? `programmée ${since}` : null,
                tone: "success",
                icon: CalendarCheck,
            };
        case "immersion_done":
            return {
                label: "Immersion effectuée",
                sublabel: since ? `réalisée ${since}` : null,
                tone: "success",
                icon: CheckCircle2,
            };
        case "validated":
        case "onboarding":
        case "hired":
            return {
                label: "Validé",
                sublabel: since,
                tone: "ok",
                icon: CheckCircle2,
            };
        default:
            return null;
    }
}

const ENGAGEMENT_TONES = {
    warning: "bg-amber-50 border-amber-300 text-amber-800",
    info: "bg-blue-50 border-blue-300 text-blue-800",
    success: "bg-emerald-50 border-emerald-300 text-emerald-800",
    ok: "bg-emerald-100 border-emerald-400 text-emerald-900",
};

/**
 * Pick the 2 AI badges most relevant for this candidate to avoid overload.
 * Priority: pepite > urgent > risque_turnover > others.
 */
function pickTopBadges(badges) {
    if (!badges || badges.length === 0) return [];
    const priority = [
        "pepite",
        "urgent",
        "risque_turnover",
        "communicant",
        "compatible_terrain",
        "potentiel_manager",
        "a_challenger",
        "faible_priorite",
        "excellence",
    ];
    const ordered = [...badges].sort(
        (a, b) =>
            (priority.indexOf(a) === -1 ? 99 : priority.indexOf(a)) -
            (priority.indexOf(b) === -1 ? 99 : priority.indexOf(b)),
    );
    return ordered.slice(0, 2);
}

export default function CandidateCard({ candidate, index = 0, onChange, selectable = false, selected = false, onToggleSelect }) {
    const signal = liveSignal(candidate);
    const phoneE164 = toE164(candidate.phone);
    const waLink = phoneE164
        ? `https://wa.me/${phoneE164.replace("+", "")}?text=${encodeURIComponent(
              `Bonjour ${candidate.first_name}, je suis de Shariti à propos de votre candidature.`,
          )}`
        : null;
    const topBadges = pickTopBadges(candidate.badges);
    const positionEqualsOffer =
        candidate.layan_offer_title &&
        candidate.position &&
        candidate.layan_offer_title.trim() === candidate.position.trim();
    const showAvailability = !DEFAULT_AVAILABILITIES.has(candidate.availability);
    const engagement = engagementBadge(candidate);
    const [busy, setBusy] = useState(false);
    // Local modal — only shown when the user clicks the Vivier icon on this
    // very card. Bulk Vivier from the list page uses its own modal.
    const [showVivierModal, setShowVivierModal] = useState(false);
    // iter99 · Modal Écarter avec motif obligatoire (CEO 2026-09-03).
    const [showSetAsideModal, setShowSetAsideModal] = useState(false);
    // CEO 2026-09-29 · Téléchargement CV en 1 clic (individuel)
    const [downloadingCv, setDownloadingCv] = useState(false);
    const hasCv = Boolean(candidate.cv_url || candidate.cv_pdf_data);

    /** Télécharge le CV du candidat en 1 clic. Utilise `?token=` pour que le
     * navigateur puisse déclencher un `<a download>` fiable même en mobile. */
    async function handleDownloadCv(e) {
        e.preventDefault();
        e.stopPropagation();
        if (downloadingCv) return;
        setDownloadingCv(true);
        try {
            const response = await api.get(`/candidates/${candidate.id}/cv`, {
                responseType: "blob",
            });
            const cd = response.headers?.["content-disposition"] || "";
            const match = /filename="?([^";]+)"?/i.exec(cd);
            const fallback = `${(candidate.first_name || "").replace(/[^A-Za-z0-9]/g, "")}_${(candidate.last_name || "").replace(/[^A-Za-z0-9]/g, "")}_CV.pdf`;
            const filename = match ? match[1] : fallback;
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success("CV téléchargé", { description: filename, duration: 3000 });
        } catch (err) {
            let detail = "Impossible de télécharger le CV";
            if (err.response?.data instanceof Blob) {
                try { detail = JSON.parse(await err.response.data.text()).detail || detail; }
                catch { /* silent */ }
            } else {
                detail = err.response?.data?.detail || err.message || detail;
            }
            toast.error(detail);
        } finally {
            setDownloadingCv(false);
        }
    }

    /**
     * "Écarter" — sidelines the candidate. Ouvre le modal pour choisir un
     * motif (iter99). Après confirmation, le candidat disparaît de la file
     * active et resurface dans /ecartes avec son motif visible.
     */
    async function handleConfirmSetAside(reason, note) {
        try {
            await api.post(`/candidates/${candidate.id}/set-aside`, {
                reason, note,
            });
            removeFromQueue(candidate.id);
            toast.success(`${candidate.first_name} écarté(e) — visible dans Écartés`);
            setShowSetAsideModal(false);
            onChange?.({ id: candidate.id, removed: true });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Impossible d'écarter");
        }
    }

    /** Restore — bring a sidelined candidate back into the active queue. */
    async function handleRestore(e) {
        e.preventDefault();
        e.stopPropagation();
        if (busy) return;
        setBusy(true);
        try {
            await api.post(`/candidates/${candidate.id}/set-aside/restore`);
            toast.success(`${candidate.first_name} remis(e) dans la file`);
            onChange?.({ id: candidate.id, removed: true });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Impossible de remettre dans la file");
        } finally {
            setBusy(false);
        }
    }

    return (
        <motion.div
            data-testid={`candidate-card-${candidate.id}`}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: index * 0.025, ease: [0.22, 1, 0.36, 1] }}
            className={`card-soft p-5 md:p-6 group transition ${selected ? "ring-2 ring-shariti-orange bg-shariti-orange-soft/30" : ""}`}
        >
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
                {/* Bulk-selection checkbox — only rendered when the list page
                    enables multi-select. Placed before the avatar so it's
                    the first thing the eye catches when selection is active. */}
                {selectable && (
                    <label
                        className="flex items-center shrink-0 cursor-pointer pl-1"
                        onClick={(e) => e.stopPropagation()}
                        title="Sélectionner pour action groupée"
                    >
                        <input
                            type="checkbox"
                            checked={selected}
                            onChange={(e) => onToggleSelect?.(candidate.id, e.target.checked)}
                            data-testid={`candidate-card-select-${candidate.id}`}
                            className="h-5 w-5 rounded-md border-2 border-shariti-orange/40 accent-shariti-orange cursor-pointer"
                        />
                    </label>
                )}

                {/* Avatar */}
                <div className="flex items-center gap-4 md:gap-5 shrink-0">
                    {candidate.avatar_url ? (
                        <img
                            src={candidate.avatar_url}
                            alt=""
                            className="h-14 w-14 md:h-16 md:w-16 rounded-2xl object-cover border-2 border-white shadow-sm"
                        />
                    ) : (
                        <div className="h-14 w-14 md:h-16 md:w-16 rounded-2xl bg-shariti-orange-soft text-shariti-orange font-extrabold text-lg flex items-center justify-center border-2 border-white shadow-sm">
                            {initials(`${candidate.first_name} ${candidate.last_name}`)}
                        </div>
                    )}
                    <div className="md:hidden">
                        <ScoreRing score={candidate.ai_score} size={56} stroke={4} label="" />
                    </div>
                </div>

                {/* Identity + meta */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                        <Link
                            to={`/candidats/${candidate.id}`}
                            className="text-base md:text-lg font-extrabold text-shariti-black hover:text-shariti-orange transition leading-tight break-words"
                            data-testid="candidate-card-name"
                        >
                            {candidate.first_name} {candidate.last_name}
                        </Link>
                        {candidate.code_rd && (
                            <span
                                data-testid={`candidate-card-code-rd-${candidate.id}`}
                                title={`Matricule Shariti ${candidate.code_rd}`}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-mono text-[10px] font-black tracking-wider bg-shariti-orange-soft text-shariti-orange border border-shariti-orange/60 shrink-0"
                            >
                                {candidate.code_rd}
                            </span>
                        )}
                        {candidate.is_test && (
                            <span
                                data-testid={`candidate-card-is-test-${candidate.id}`}
                                title="Candidat marqué comme test — invisible du Rattrapage, PayFit et API /service/v1 tant que ce flag est actif"
                                className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-shariti-orange text-white shrink-0 shadow-sm"
                            >
                                🧪 TEST
                            </span>
                        )}
                    </div>

                    {/* Application date — always shown so RH can see when the
                        candidate applied without opening the profile.
                        Format: "Postulé le 5 juin 2026 · il y a 47 j" */}
                    {candidate.applied_at && (
                        <div
                            data-testid={`candidate-card-applied-${candidate.id}`}
                            className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-500"
                        >
                            <CalendarClock className="h-3 w-3 text-slate-400" />
                            Postulé le <strong className="text-slate-700">{formatDate(candidate.applied_at)}</strong>
                            <span className="text-slate-400">·</span>
                            <span className="text-slate-400">{timeAgo(candidate.applied_at)}</span>
                        </div>
                    )}

                    {/* Rejection block — red, high-visibility, only for rejected candidates.
                        Shows "who + when + why" so no one has to open the profile
                        to understand a Refusé decision. */}
                    {candidate.status === "rejected" && candidate.rejection_meta && (
                        <div
                            data-testid={`candidate-card-rejection-${candidate.id}`}
                            className="mt-2 rounded-lg border border-red-200 bg-red-50/70 px-2.5 py-1.5"
                        >
                            <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-shariti-red">
                                <UserX className="h-3 w-3" />
                                Refusé — {rejectionReasonLabel(candidate.rejection_meta.reason)}
                            </div>
                            <div className="mt-0.5 text-[11px] font-semibold text-red-900/80">
                                Par <strong>{candidate.rejection_meta.by_name || "—"}</strong>
                                {candidate.rejection_meta.at && (
                                    <> · le {formatDate(candidate.rejection_meta.at)}</>
                                )}
                            </div>
                            {candidate.rejection_meta.comment && (
                                <div className="mt-0.5 text-[11px] text-red-900/70 italic line-clamp-2" title={candidate.rejection_meta.comment}>
                                    « {candidate.rejection_meta.comment} »
                                </div>
                            )}
                        </div>
                    )}

                    {/* HIGH-VISIBILITY engagement badge — own row so it
                        wraps cleanly on narrow screens. */}
                    {engagement && (
                        <div className="mt-1.5">
                            <span
                                data-testid={`candidate-card-engagement-${candidate.id}`}
                                title={engagement.sublabel || ""}
                                className={`inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest px-2 py-1 rounded-full border-2 ${ENGAGEMENT_TONES[engagement.tone]}`}
                            >
                                <engagement.icon className="h-3 w-3" />
                                {engagement.label}
                            </span>
                            {engagement.sublabel && (
                                <span
                                    data-testid={`candidate-card-engagement-sub-${candidate.id}`}
                                    className="ml-2 text-[11px] font-semibold text-slate-500"
                                >
                                    {engagement.sublabel}
                                </span>
                            )}
                        </div>
                    )}

                    {/* iter94 · Repostulation badge — visible quand un
                        candidat (déjà en base : refusé / vivier / écarté)
                        repostule via Layan sur une offre Publiée. Le
                        recruteur sait qu'il y a un historique à consulter
                        mais que le candidat est de nouveau actif. */}
                    {candidate.reapplied && (
                        <div className="mt-1.5">
                            <span
                                data-testid={`candidate-card-reapplied-${candidate.id}`}
                                title={
                                    candidate.reapplied_from_status
                                        ? `Statut précédent : ${candidate.reapplied_from_status}${candidate.reapply_count > 1 ? ` · ${candidate.reapply_count}e repostulation` : ""}`
                                        : "Repostulé sur une offre active"
                                }
                                className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest px-2 py-1 rounded-full border-2 bg-purple-50 text-purple-700 border-purple-300"
                            >
                                🔁 Repostulé
                                {candidate.reapply_count > 1 && (
                                    <span className="ml-0.5 opacity-70">×{candidate.reapply_count}</span>
                                )}
                            </span>
                            {candidate.reapplied_offer_title && (
                                <span
                                    className="ml-2 text-[11px] font-semibold text-slate-500"
                                    data-testid={`candidate-card-reapplied-offer-${candidate.id}`}
                                >
                                    → {candidate.reapplied_offer_title}
                                </span>
                            )}
                        </div>
                    )}

                    {/* iter97 · CEO 2026-09-02 : « Sur /refuses, on doit voir
                        POURQUOI la personne a été refusée d'un seul coup
                        d'œil, ligne par ligne. » Badge rouge visible sur
                        toute carte de candidat rejeté. Le label vient du
                        `rejection_meta.reason_label` denormalisé par
                        l'endpoint reject (fallback: reason brut). */}
                    {candidate.status === "rejected" && (candidate.rejection_meta || candidate.reject_reason) && (
                        <div className="mt-1.5">
                            <span
                                data-testid={`candidate-card-reject-reason-${candidate.id}`}
                                title={
                                    candidate.rejection_meta?.comment
                                        ? `Note: ${candidate.rejection_meta.comment}`
                                        : (candidate.rejection_meta?.by_name
                                            ? `Refusé par ${candidate.rejection_meta.by_name}`
                                            : "")
                                }
                                className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-wide px-2.5 py-1 rounded-full border-2 bg-red-50 text-red-700 border-red-300"
                            >
                                ✕ Refusé —{" "}
                                {candidate.rejection_meta?.reason_label
                                    || candidate.reject_reason?.replace(/_/g, " ")
                                    || "Motif non précisé"}
                            </span>
                            {candidate.rejection_meta?.by_name && (
                                <span className="ml-2 text-[11px] font-semibold text-slate-500">
                                    par {candidate.rejection_meta.by_name}
                                </span>
                            )}
                        </div>
                    )}

                    {/* iter99 · CEO 2026-09-03 : « Motif d'écartement en grand
                        sur la ligne du candidat concerné » — miroir du badge
                        reject, en amber pour distinguer un ÉCARTAGE (récupérable)
                        d'un REFUS (définitif). */}
                    {candidate.set_aside && (candidate.set_aside_reason || candidate.set_aside_note) && (
                        <div className="mt-1.5">
                            <span
                                data-testid={`candidate-card-set-aside-reason-${candidate.id}`}
                                title={
                                    candidate.set_aside_note
                                        ? `Note: ${candidate.set_aside_note}`
                                        : (candidate.set_aside_by_name
                                            ? `Écarté par ${candidate.set_aside_by_name}`
                                            : "")
                                }
                                className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-wide px-2.5 py-1 rounded-full border-2 bg-amber-50 text-amber-800 border-amber-300"
                            >
                                <Archive className="h-3 w-3" />
                                Écarté —{" "}
                                {candidate.set_aside_reason_label
                                    || candidate.set_aside_reason?.replace(/_/g, " ")
                                    || "Motif non précisé"}
                            </span>
                            {candidate.set_aside_by_name && (
                                <span className="ml-2 text-[11px] font-semibold text-slate-500">
                                    par {candidate.set_aside_by_name}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Position only if different from offer title (avoid duplicate) */}
                    {!positionEqualsOffer && candidate.position && (
                        <p className="mt-0.5 text-sm font-medium text-slate-500 break-words">
                            {candidate.position}
                        </p>
                    )}

                    {/* Source badge — always visible so the user instantly
                        knows where the candidate came from (Site Shariti
                        web form vs Layan job board). */}
                    {candidate.source === "Site Shariti" ? (
                        <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-shariti-orange-soft border border-shariti-orange/30 text-shariti-orange text-[10px] font-extrabold uppercase tracking-widest" data-testid="card-source-site">
                            🌐 Site Shariti
                        </div>
                    ) : candidate.source && candidate.source.startsWith("Layan") ? (
                        <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-300 text-slate-700 text-[10px] font-extrabold uppercase tracking-widest" data-testid="card-source-layan">
                            <Briefcase className="h-3 w-3" /> Layan
                        </div>
                    ) : candidate.source ? (
                        <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-600 text-[10px] font-extrabold uppercase tracking-widest" data-testid="card-source-other">
                            {candidate.source}
                        </div>
                    ) : null}

                    {/* Offer row */}
                    {candidate.layan_offer_title && (
                        <div className="mt-2 flex items-start gap-2 text-xs">
                            <Briefcase className="h-3.5 w-3.5 text-slate-400 shrink-0 mt-0.5" />
                            <span className="text-slate-600 font-semibold break-words" title={candidate.layan_offer_title}>
                                {candidate.layan_offer_title}
                                {candidate.layan_offer_region && (
                                    <span
                                        className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full bg-shariti-orange-soft text-shariti-orange text-[9px] font-black uppercase tracking-widest border border-shariti-orange/30 align-middle"
                                        data-testid={`candidate-offer-region-${candidate.id}`}
                                    >
                                        {candidate.layan_offer_region}
                                    </span>
                                )}
                            </span>
                            {candidate.layan_offer_status && (
                                <span
                                    className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                                        OFFER_STATUS_TONE[candidate.layan_offer_status] || "bg-slate-100 text-slate-600 border-slate-200"
                                    }`}
                                    data-testid="candidate-offer-status"
                                >
                                    {candidate.layan_offer_status}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Phone — own line so the Aircall/Chrome tel: icon
                        injected by the browser extension doesn't overlap
                        the city/signal metadata next to it. */}
                    <div className="mt-3">
                        <a
                            href={`tel:${phoneE164}`}
                            data-testid={`candidate-card-phone-${candidate.id}`}
                            className="inline-flex items-center gap-1.5 text-shariti-black font-extrabold tracking-tight text-sm hover:text-shariti-orange transition pr-8"
                        >
                            <Phone className="h-3.5 w-3.5 text-shariti-orange" />
                            {candidate.phone}
                        </a>
                    </div>

                    {/* Secondary metadata row - city / availability / live signal */}
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-semibold">
                        <span className="inline-flex items-center gap-1 text-slate-500">
                            <MapPin className="h-3.5 w-3.5" /> {candidate.city}
                        </span>
                        {showAvailability && (
                            <span className="inline-flex items-center gap-1 text-slate-500">
                                <Clock className="h-3.5 w-3.5" /> {candidate.availability}
                            </span>
                        )}
                        {signal && (
                            <span
                                data-testid={`candidate-card-signal-${candidate.id}`}
                                className={`text-xs font-semibold ${SIGNAL_TONE[signal.tone]}`}
                            >
                                · {signal.label}
                            </span>
                        )}
                    </div>

                    {/* AI badges - max 2, with subtle Sparkles prefix */}
                    {topBadges.length > 0 && (
                        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                            <span
                                className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-400 mr-0.5"
                                title="Étiquettes détectées par l'IA Claude"
                            >
                                <Sparkles className="h-3 w-3 text-shariti-orange" /> IA
                            </span>
                            {topBadges.map((b) => (
                                <SmartBadge key={b} type={b} size="sm" />
                            ))}
                        </div>
                    )}
                </div>

                {/* Score + primary action */}
                <div className="flex md:flex-col items-center md:items-end justify-between md:justify-center gap-3 md:gap-4 shrink-0 md:min-w-[200px]">
                    <div className="hidden md:block">
                        <ScoreRing score={candidate.ai_score} size={76} stroke={5} label="" />
                    </div>
                    <div className="flex items-center gap-1.5 md:gap-2 flex-wrap justify-end">
                        {/* Simple call — Twilio click-to-call via browser mic.
                            No scorecard, no wizard: just dial. Green so it's
                            visually distinct from the qualification flow. */}
                        <CallButton
                            candidate={candidate}
                            variant="wide"
                            dataTestid={`candidate-card-call-${candidate.id}`}
                        />
                        {/* CV download — CEO 2026-09-29 · Bouton 1 clic pour
                            récupérer le CV du candidat sans ouvrir la fiche.
                            Placé juste à côté de "Appel rapide" (à gauche des
                            autres actions) pour rester dans l'axe naturel de
                            lecture. Grisé si aucun CV attaché. */}
                        <button
                            type="button"
                            onClick={handleDownloadCv}
                            disabled={!hasCv || downloadingCv}
                            data-testid={`candidate-card-cv-${candidate.id}`}
                            title={hasCv ? "Télécharger le CV du candidat" : "Aucun CV attaché"}
                            aria-label={hasCv ? "Télécharger le CV" : "Aucun CV disponible"}
                            className={`h-9 w-9 inline-flex items-center justify-center rounded-full transition ${
                                hasCv
                                    ? "bg-shariti-orange-soft text-shariti-orange hover:bg-shariti-orange hover:text-white shadow-sm"
                                    : "bg-shariti-cream text-slate-300 cursor-not-allowed"
                            }`}
                        >
                            {downloadingCv ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <FileText className="h-4 w-4" />
                            )}
                        </button>
                        {/* Inline audio play — reads the LAST recording for this
                            candidate. Hidden when there is no call history. */}
                        <PlayLastCallButton
                            candidate={candidate}
                            dataTestid={`candidate-card-play-${candidate.id}`}
                        />
                        {/* Qualification flow — opens the structured scorecard
                            + live interview script. Different from the simple
                            call above: it walks the recruiter through the
                            PREREC pre-qualification questionnaire. */}
                        <Link
                            to={`/candidats/${candidate.id}/scorecard/prerec`}
                            data-testid={`candidate-card-scorecard-${candidate.id}`}
                            title="Lancer la pré-qualification (scorecard + script d'entretien)"
                            className="h-9 inline-flex items-center gap-1.5 rounded-full px-3.5 bg-shariti-orange text-white hover:bg-[#d66528] font-bold text-xs transition shadow-btn"
                        >
                            <ClipboardList className="h-3.5 w-3.5" />
                            Qualifier
                        </Link>
                        {/* Fast-track — candidat déjà connu du recruteur : on
                            saute entretien + immersion et on envoie le dossier
                            d'onboarding en un clic. Le composant se cache tout
                            seul pour les candidats déjà « hired » ou « rejected ». */}
                        <FastTrackButton
                            candidate={candidate}
                            variant="chip"
                            onDone={onChange}
                            dataTestid={`candidate-card-fast-track-${candidate.id}`}
                        />
                        {waLink && (
                            <a
                                href={waLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`candidate-card-whatsapp-${candidate.id}`}
                                title="WhatsApp"
                                className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-[#25D366] text-white hover:bg-[#1ebe57] transition"
                            >
                                <MessageCircle className="h-4 w-4" />
                            </a>
                        )}
                        <Link
                            to={`/candidats/${candidate.id}`}
                            data-testid={`candidate-card-open-${candidate.id}`}
                            title="Ouvrir la fiche"
                            className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-shariti-cream text-slate-500 hover:text-shariti-orange hover:bg-shariti-orange-soft transition"
                        >
                            <Eye className="h-4 w-4" />
                        </Link>
                        {/* Vivier — direct 1-click move to the talent pool
                            from the call queue. Opens the same reason picker
                            the profile uses, so the audit trail is consistent. */}
                        {!candidate.in_talent_pool && (
                            <button
                                type="button"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowVivierModal(true); }}
                                disabled={busy}
                                data-testid={`candidate-card-vivier-${candidate.id}`}
                                title="Mettre au vivier"
                                className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-shariti-cream text-slate-500 hover:text-amber-700 hover:bg-amber-100 transition disabled:opacity-40"
                            >
                                <Bookmark className="h-4 w-4" />
                            </button>
                        )}
                        {/* Écarter / Remettre dans la file — single-click sideline */}
                        {candidate.set_aside ? (
                            <button
                                onClick={handleRestore}
                                disabled={busy}
                                data-testid={`candidate-card-restore-${candidate.id}`}
                                title="Remettre ce candidat dans la file d'appels"
                                className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition disabled:opacity-40"
                            >
                                <RotateCcw className="h-4 w-4" />
                            </button>
                        ) : (
                            <button
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowSetAsideModal(true); }}
                                disabled={busy}
                                data-testid={`candidate-card-set-aside-${candidate.id}`}
                                title="Écarter ce candidat de la file — un motif sera demandé"
                                className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-shariti-cream text-slate-400 hover:text-shariti-red hover:bg-red-50 transition disabled:opacity-40"
                            >
                                <Archive className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {showVivierModal && (
                <TalentPoolModal
                    candidate={candidate}
                    onClose={() => setShowVivierModal(false)}
                    onParked={() => {
                        // Card disappears from the active queue once parked.
                        onChange?.({ id: candidate.id, removed: true });
                        removeFromQueue(candidate.id);
                    }}
                />
            )}

            {/* iter99 · Modal Écarter avec motif obligatoire */}
            <SetAsideReasonModal
                open={showSetAsideModal}
                onOpenChange={setShowSetAsideModal}
                candidate={candidate}
                onConfirm={handleConfirmSetAside}
            />
        </motion.div>
    );
}
